import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	isDatabase,
	isSlides,
	isWhiteboard,
} from '@confluence/named-routes/entry-points/routes-utils.ts';

/**
 * WARNING: If this ends up running on a page route which is not for either a Live Doc, or Page (edit or published view),
 * this will return 'renderer' which is the fall back.
 *
 * If running on a non page or doc route
 * - if it is a database: will return 'database'.
 * - if it is a whiteboard: will return 'whiteboard'.
 * - if it is a TinyMCE page: will return 'tinyMCE'.
 * - otherwise: it will return 'renderer'.
 *
 * This is only suitable for comment purposes and the fall back to 'renderer' may not suit
 * use cases outside of commenting (where for the comment experiences -- they are only expected
 * to run on pages, live docs, whiteboards, and databases).
 */
export function usePageClass():
	| 'editor'
	| 'renderer'
	| 'tinyMCE'
	| 'database'
	| 'whiteboard'
	| 'slide' {
	// Note -- this hook does not inform the consumer if they are on a live doc page which
	// is using the editor.
	const isEditorPage = useIsEditorPage();
	const isCurrentPageLive = useIsCurrentPageLive();
	// useRouteName use RoutesContext which is more reliable than reading window.location.pathname directly.
	const routeName = useRouteName({ selector: (name) => name });

	if (isEditorPage) {
		return 'editor';
	}

	if (isCurrentPageLive) {
		return 'editor';
	}

	const isTinyMCEPage = __checkIfTinyMCEEditorPage();
	if (isTinyMCEPage) {
		// TinyMCE pages are not considered editor pages or docs in the context of this hook
		return 'tinyMCE';
	}

	// Pass routeName to isDatabase/isWhiteboard - they will use it to avoid race conditions during SPA transitions
	if (isDatabase(routeName)) {
		return 'database';
	}

	if (isWhiteboard(routeName)) {
		return 'whiteboard';
	}

	if (isSlides(routeName)) {
		return 'slide';
	}

	return 'renderer';
}

function __checkIfTinyMCEEditorPage(): boolean {
	const possibleTinyMCESelectors = [`[data-testid="TinyMCERendererTestId"]`, '.tinymce-editor'];

	// This check is only relevant in the context of comment experiences
	// and so if we are in an environment without a document -- we
	// can safely treat it as not a tinymce page.
	if (typeof document === 'undefined') {
		return false;
	}

	const isTinyMCEPage = possibleTinyMCESelectors.some(
		(selector) => document.querySelector(selector) !== null,
	);

	return isTinyMCEPage;
}
